import { Fragment, useCallback, useMemo, useState, useEffect } from "react";
import { SubscriptionType } from "~/types/billing";
import { usePlans } from "~/api/billing/queries";
import { usePlan } from "~/contexts/Plans";
import FlexContainer from "~/components/ui/FlexContainer";
import Container from "~/components/ui/Container";
import Toggle from "~/components/common/Toggle";
import PlanCard from "./planCard";
import Loader from "~/components/ui/Loader";

import * as S from "./plans.style";
import { centsToDecimal } from "~/helpers/numbers";
import { useMediaQuery } from "~/hooks/useMediaQuery";

// Assets
import arrowPlans from "~/assets/svg/arrow_plans.svg";

export const Plans = () => {
  const { data: plansData, isLoading } = usePlans();
  const { setPlan, plan: contextPlan } = usePlan();
  const isLaptop = useMediaQuery("laptop");
  const [isAnnual, setIsAnnual] = useState<boolean>(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | undefined>(
    "6b67c0c0-be02-47ad-9227-52a300566185"
  );

  useEffect(() => {
    if (!plansData) return;
    //@ts-ignore
    setPlan(plansData?.find((plan) => plan.id === selectedPlanId));
  }, [plansData, isLoading]);

  const filteredPlans = useMemo(() => {
    if (isAnnual) return plansData?.filter((plan) => plan.interval === SubscriptionType.ANNUAL);
    return plansData?.filter((plan) => plan.interval === SubscriptionType.MONTHLY);
  }, [plansData, isAnnual]);

  const toggleControl = useCallback(() => {
    setIsAnnual((prev) => !prev);
    //@ts-ignore
    const prevPlan = plansData.filter((plan) => plan.id === selectedPlanId)[0];
    const newPlan = plansData?.find((plan) => {
      const formattedPlanName = prevPlan?.name === "Starter" ? "Pro" : prevPlan?.name;
      return formattedPlanName === plan?.name && (plan.interval === "yearly") !== isAnnual;
    });
    //@ts-ignore
    setSelectedPlanId(newPlan.id);
    //@ts-ignore
    setPlan(newPlan);
  }, [isAnnual, plansData, selectedPlanId]);

  useEffect(() => {
    if (!filteredPlans) return;
    const plan = filteredPlans.find((plan) => plan.name === contextPlan?.name);
    if (plan) setPlan(plan);
  }, [filteredPlans, selectedPlanId, setPlan]);

  if (isLoading) return <Loader />;

  return (
    <Fragment>
      <FlexContainer
        justifyContent="space-between"
        alignItems={isLaptop ? "center" : "flex-start"}
        flexDirection={isLaptop ? "row" : "column"}
        flexWrap="wrap"
        gap="12px"
        width="100%"
      >
        <S.SelectText>Select Spocket plan</S.SelectText>
        <Container padding="0">
          <S.ToggleContentText className={`${!isAnnual ? "selected" : ""}`}>Monthly</S.ToggleContentText>
          <Toggle onChange={toggleControl} value={isAnnual} />
          <S.ToggleContentText className={`${isAnnual ? "selected" : ""}`}>Annually</S.ToggleContentText>
          <S.PromotionalTextContainer>
            <S.PromotionText>Get up to 8 months off</S.PromotionText>
          </S.PromotionalTextContainer>
        </Container>
      </FlexContainer>
      <S.PlansGrid isAnnual={isAnnual}>
        {filteredPlans?.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            selectedPlanId={selectedPlanId}
            setSelectedPlanId={setSelectedPlanId}
          />
        ))}
      </S.PlansGrid>
      <S.GreyText isAnnual={isAnnual}>
        {`"All plans come with a ${
          plansData?.find((plan) => plan.id === selectedPlanId)?.trial_days
        }-day trial. After trial you will pay $${centsToDecimal(
          //@ts-ignore
          plansData?.find((plan) => plan.id === selectedPlanId)?.cost_per_month
        )}"`}
      </S.GreyText>
    </Fragment>
  );
};
